import { Component, EventEmitter, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { NgForm } from '@angular/forms';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { ScenarioInput } from '../../../../models/results/scenario-comparison/scenario-input.model';
import { AnessaTranslationService } from '../../../../helper/translation/anessa-translation.service';
import { Unit } from '../../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ScenarioComparisonService } from '../../scenario-comparison.service';
import Utils from '../../../../util';
import { UserpilotReloader } from '../../../../helper/userpilot-reloader.component';
import { ProjectFinancialService } from '../../../../project/financial/project-financial.service';
import { MatOptionSelectionChange } from '@angular/material/core';
import { Scenario } from '../../../../models/results/scenario-comparison/scenario.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChangePercent } from '../../../../models/interfaces/results/scenario-comparison/change-percent.interface';
import { ScenarioModifyErrorHintIds } from '../../../../models/enums/results/scenario-comparison/scenario-modify-error-hint-ids.enum';
import { ScenarioModifyErrorSummaryIds } from '../../../../models/enums/results/scenario-comparison/scenario-modify-error-summary-ids.enum';
import { BiogasShare } from '../../../../models/project/plant-specifications/biogas-share.model';
import { SliderData } from '../../../../models/data-structures/general/slider-data.model';
import { DeactivationDialogService } from '../../../../helper/can-deactivate/deactivation-dialog.service';


@Component({
  selector: 'app-scenario',
  templateUrl: './scenario-modify-dialog.component.html',
  styleUrls: ['./scenario-modify-dialog.component.scss'],
  providers: [ScenarioComparisonService]
})
export class ScenarioModifyDialogComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @ViewChild('sensForm', {static: true}) sensForm?: NgForm;
  onDestroyNotifier = new EventEmitter();


  /* ---- page loaded properties ---- */
  scenarioServiceReady = false;


  /* ---- current scenario  ---- */
  // the current scenario inputs are used for the input table data source
  scenario: Scenario;
  originalScenarioName?: string;
  editIndex: number = undefined;
  invalidInputs: string[];
  affectedInputs: string[];

  /* ---- scenario item data objs ---- */

  /* data obj for the input select dropdown */
  inputsDropdownData: ScenarioInput[] = [];

  /* product price data inputs */
  productPriceData: ScenarioInput[] = [];

  /* lcfsrin data inputs */
  productLcfsRinData: ScenarioInput[] = [];

  /* input data updated based on the inputted filter criteria */
  filteredInputsData: ScenarioInput[] = [];

  /* input filter contents */
  inputFilter: string | null = null;


  /* ---- table properties ---- */

  tableHeaders = ['include', 'variables', 'scenarioValue', 'value', 'unit'];
  /* scenario item uuid is used as the identifier in this record. This can stay local
  * and doesn't need to be saved after the user leaves */
  selectedTableRows: Record<string, boolean> = {};


  /* biogas share data properties */
  biogasShareData: BiogasShare[];
  biogasSliderData: SliderData[] = [];
  biogasSlidersAvailable = false;
  biogasScenarioInputs: ScenarioInput[] = [];

  /* ---- form properties ---- */

  /* active form errors */
  formErrors: string[] = [];

  /* a record of translations for each input, it's populated with all input translations */
  inputTranslations: Record<string, string> = {};

  /* object used to control the percent change input and its properties */
  changePercent: ChangePercent = {name: 'changePercent', value: null, errors: []};

  /* a record of active error hints for the scenario value input
  *  the scenario item uuid is used as the string identifier */
  scenarioValueErrorHints: Record<string, Record<string, boolean>> = {};


  /* disable inputs obj */
  disabled: Record<string, boolean> = {};

  /* tooltips obj */
  tooltips: Record<string, string> = {};

  /* selected inputs obj */
  selected: Record<string, boolean> = {};

  /* boolean to keep track of any changes to the scenario */
  unSavedChanges = false;

  /* variable to keep track of the initial state of biogasSliderData when the dialog was first opened */
  initialBiogasSliderData: SliderData[] = [];

  /* an object used to toggle different error checks throughout
  * the component flow */
  errorsToCheck = {
    'no-inputs': false,
    'empty-input': false,
    'empty-apply-to-all': false,
    'invalid-inputs': false,
    'negative-input': true,
    'exceeds-project-lifetime': true
  };

  /* Dictionary of error summary translation keys */
  errorsSummaryDict = Object.freeze({
    [ScenarioModifyErrorSummaryIds.NO_INPUTS]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.ACTUAL.NO_INPUTS',
    [ScenarioModifyErrorSummaryIds.EMPTY_INPUT]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.ACTUAL.EMPTY_INPUT',
    [ScenarioModifyErrorSummaryIds.NEGATIVE_INPUT]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.ACTUAL.NEGATIVE_INPUT',
    [ScenarioModifyErrorSummaryIds.IS_NAN]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.ACTUAL.IS_NAN',
    [ScenarioModifyErrorSummaryIds.EXCEEDS_PROJECT_LIFETIME]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.ACTUAL.EXCEEDS_PROJECT_LIFETIME',
    [ScenarioModifyErrorSummaryIds.SMALL_LOAN_DURATION]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.ACTUAL.SMALL_LOAN_DURATION',
    [ScenarioModifyErrorSummaryIds.INVALID_INPUTS]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.ACTUAL.INVALID_INPUTS'
  });

  /* Dictionary of error hint translation keys */
  errorsHintsDict = Object.freeze({
    [ScenarioModifyErrorHintIds.EMPTY_INPUT]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.HINT.EMPTY_INPUT',
    [ScenarioModifyErrorHintIds.NEGATIVE_INPUT]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.HINT.NEGATIVE_INPUT',
    [ScenarioModifyErrorHintIds.IS_NAN]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.HINT.IS_NAN',
    [ScenarioModifyErrorHintIds.NON_POSITIVE]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.HINT.NON_POSITIVE',
    [ScenarioModifyErrorHintIds.PERCENT_OVER_100]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.HINT.PERCENT_OVER_100',
    [ScenarioModifyErrorHintIds.EXCEEDS_PROJECT_LIFETIME]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.HINT.EXCEEDS_PROJECT_LIFETIME',
    [ScenarioModifyErrorHintIds.SMALL_LOAN_DURATION]: 'SCENARIO_COMPARISON.SCENARIO_MODIFY.ERRORS.HINT.SMALL_LOAN_DURATION'
  });

  /**
   * Constructor; Provides data in which contains a scenario if an existing scenario is being
   * modified.
   * @param translationService
   * @param scenarioComparisonService
   * @param toastrService
   * @param projectFinService
   * @param dialogRef
   * @param dataIn is used to pass in a scenario if an edit is being performed
   * @param ref
   */
  constructor(
    private translationService: AnessaTranslationService,
    private scenarioComparisonService: ScenarioComparisonService,
    private projectFinService: ProjectFinancialService,
    private dialogRef: MatDialogRef<ScenarioModifyDialogComponent>,
    private dialogService: DeactivationDialogService,
    @Inject(MAT_DIALOG_DATA) public dataIn: {
      scenario: Scenario,
      editIndex: number,
      invalidInputs: string[],
      affectedInputs: string[]
    },
  ) {
    super();
  }

  ngOnInit(): void {
    // listen for scenario comparison service ready
    this.scenarioComparisonService.serviceReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(ready => {
        this.scenarioServiceReady = ready;
        if (ready) {
          this.setup();
        }
      });

    // listen for scenario comparison service updates (such as save)
    this.scenarioComparisonService.updateNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.setup();
          this.inputsDropdownData = [];
        }
      });

      this.dialogRef.backdropClick().subscribe((event) => {
        if (this.hasUnsavedChanges()) {
          this.dialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'))
            .pipe(take(1)).subscribe(result => {
              if (result) {
                this.dialogRef.close();
              }
            });
        } else {
          this.dialogRef.close();
        }
      });
  }

  setUnsavedChanges(): void {
    this.unSavedChanges = true;
  }

  hasUnsavedChanges(): boolean {
    return this.unSavedChanges;
  }

  /**
   * Triggers the onDestroyNotifier to clean up all subscriptions
   */
  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * When new inputs are selected from the inputs dropdown sort and validate the table with
   * the updated inputs
   */
  onSelectedInputsChange(): void {
    this.setUnsavedChanges();
    if (!this.scenario?.inputs || this.scenario.inputs.length === 0) {
      this.addFormError('no-inputs');
    } else {
      this.removeFormError('no-inputs');
    }

    this.clearUnselectedInputValues();

    this.sortSelectedInputs();
    this.validateTable();
  }

  /**
   * Filter the inputs shown in the input selection dropdown based on the user inputted filter
   * criteria. The filter operation in this function is placed within an async function that called
   * within, this reduces any lag the view would experience while filter is happening.
   */
  filterInputs(): void {
    const asyncFunction = () => {
      if (!this.inputsDropdownData) {
        return;
      }
      // set search criteria to current input filter and begin processing
      let search = this.inputFilter;
      if (!search) { // if no search criteria perform shallow copy of input data
        this.filteredInputsData = this.inputsDropdownData.slice();
        this.onSelectedInputsChange();
        return;
      } else { // else to lower case to perform search
        search = search.toLowerCase();
      }

      // filter input data by search criteria using to lower case and call setSelectedInputs
      this.filteredInputsData = this.inputsDropdownData.filter(
        input => this.inputTranslations[input.id].toLowerCase().includes(search)
      );
      this.onSelectedInputsChange();

    };
    // call async function to allow view to continue
    asyncFunction();
  }

  /**
   * Select or deselect all inputs based on the status of the provide mat checkbox change.
   * After selecting/deselecting all inputs sort and validate table data.
   * @param $event is a MatCheckboxChange item that provides the "checked" boolean used to determine
   * if the function is selecting or deselecting all inputs.
   */
  onInputsCheckAll($event: MatCheckboxChange): void {
    if ($event.checked) {
      this.selectAllItems();
    } else {
      this.deselectAllItems();
    }

    this.sortSelectedInputs();
    this.validateTable();

    // angular detection mechanism doesn't detect items being added or removed, so
    // we have to fully re-set the array
    setTimeout(() => {
      this.scenario.inputs = [...this.scenario.inputs];
    });
  }

  /**
   * Returns a boolean used to determine if all shown inputs are selected. This is mainly used for
   * determining if the GUI should show the check all checkbox as checked. This function takes
   * an input being disabled into account, if an input is disabled it cannot be checked, and therefore
   * it is not taken into account.
   */
  allInputsSelected(): boolean {
    for (const item of this.filteredInputsData) {
      if (!this.scenario.inputs.includes(item)) {
        // if the input is disabled it shouldn't be taken into account
        if (this.disabled[item.uuid]) continue;
        return false;
      }
    }
    return true;
  }

  /**
   * Returns boolean used to determine if inputs are partially checked. Disabled inputs are not
   * able to be check and therefor are not taken into account.
   */
  isCheckAllIndeterminate(): boolean {
    let atLeastOneIncluded = false;
    let allIncluded = true;

    if (this.filteredInputsData && this.filteredInputsData.length > 0) {
      for (const item of this.filteredInputsData) {
        // skip disabled inputs, they are not taken into account
        if (this.disabled[item.uuid]) continue;
        if (!this.scenario.inputs.includes(item)) {
          atLeastOneIncluded = true;
        } else {
          allIncluded = false;
        }
      }
    }

    return atLeastOneIncluded && !allIncluded;
  }

  /**
   * This function checks selected inputs on change and updated the disabled state of
   * generated product inputs based on which inputs are actively selected. If a product contains
   * lcfsrin inputs the user shouldn't be able to select both the product price and any lcfsrin
   * inputs at the same time. When an lcfsrin input is actively selected the products price input
   * should be disabled. If a product that has lcfsrin inputs has its price input selected, the
   * products lcfsrin inputs should be disabled.
   * @param event is a MatOptionSelectionChange object that contains the current input, as
   * a ScenarioItem, and the checked state as a boolean.
   */
  onInputSelect(event: MatOptionSelectionChange) {
    const input: ScenarioInput = event.source.value;
    const checked: boolean = event.source.selected;

    // if the current input is a product price and it's being checked
    if (this.isProductPrice(input.path) && checked) {
      // update the state of the lcfsrin inputs for the current generated product
      this.updateLcfsrinState(checked, input.path);
      // else if input is an lcfsrin input or the input has the same product uuid
    } else if (this.lcfsrinInputsEnabledForProduct(input, checked)) {
      // update the state of the product price input
      this.updateProductPriceState(true, input.path);
    } else {
      // else check the state of the product for the provided input and enable if possible
      this.checkProductPriceEnabledState(input);
    }
  }

  /**
   * Selects all items currently selected in the table. Updates the selectedTableRows object with
   * each item selected.
   * @param $event is a MatCheckboxChange object that is used to determine if the checked state
   * if true or false.
   */
  rowsCheckAll($event: MatCheckboxChange) {
    this.setUnsavedChanges();
    for (const input of this.scenario.inputs) {
      this.selectedTableRows[input.uuid] = $event.checked;
    }
  }

  /**
   * Returns a boolean representing all table rows being selected within the table.
   */
  allRowsSelected(): boolean {
    return this.scenario?.inputs?.length > 0
      && this.scenario?.inputs?.length === this.numOfSelectedRows();
  }

  /**
   * Returns the number of currently selected table rows within the table.
   */
  numOfSelectedRows() {
    let selectedCount = 0;
    for (const key of Object.keys(this.selectedTableRows)) {
      if (this.selectedTableRows[key]) selectedCount++;
    }
    return selectedCount;
  }

  /**
   * When a table row is selected updates the selectedTableRows object to show it as selected.
   * @param $event is a MatCheckboxChange object that is used to get the checked state of
   * the table row.
   * @param uuid is the uuid of the table row being selected/unselected.
   */
  onRowSelected($event: MatCheckboxChange, uuid: string): void {
    this.setUnsavedChanges();
    this.selectedTableRows[uuid] = $event.checked;
  }

  /**
   * Triggers the table item input check and initiates error check to show summarized errors.
   * @param scenarioItem is the scenario item currently being altered.
   */
  onInputChange(scenarioItem: ScenarioInput) {
    this.setUnsavedChanges();
    this.checkInput(scenarioItem);
    this.summariseErrors();
  }

  /**
   * Returns a list of error hints for the provided table element.
   * @param element is a scenario item being displayed in the table as an element.
   */
  getErrorKeysForElement(element: ScenarioInput): string[] {
    if (!element?.uuid || !this.scenarioValueErrorHints[element.uuid]) return [];

    return Object.keys(this.scenarioValueErrorHints[element.uuid]);
  }

  /**
   * A simple is numeric check that uses the general Utils isNumeric check.
   * @param value is any value that will be checked for being numeric.
   */
  checkIfNum(value: any) {
    return Utils.isNumeric(value);
  }

  /**
   * Translates the provided translation id and performs unit display name processing on the value.
   * @param translationId
   */
  processUnitName(translationId: string) {
    return Unit.parseUnitDisplayName(this.translationService.get(translationId));
  }

  /**
   * Removes selected table rows from the list of selected inputs and the input table. If
   * no rows are selected all inputs will be unselected.
   */
  onRemoveSelectedInputsClick() {
    this.setUnsavedChanges();
    // if no rows selected, remove button turns into remove all inputs button
    if (this.numOfSelectedRows() === 0) {
      this.scenario.inputs = [];
    } else { // else only remove selected inputs
      for (const key of Object.keys(this.selectedTableRows)) {
        if (this.selectedTableRows[key]) {
          const i = this.scenario.inputs.findIndex((item) =>
            item.uuid === key
          );
          if (i >= 0 && i < this.scenario.inputs.length) {
            // remove input from selected scenario items as well as the selected table rows object
            this.scenario.inputs.splice(i, 1);
            this.selectedTableRows[key] = undefined;
          } else {
            throw Error('unable to remove selected item');
          }
        }
      }
    }

    // angular detection mechanism still doesn't detect items being added or removed, so
    // we have to fully re-set the array
    setTimeout(() => {
      this.scenario.inputs = [...this.scenario.inputs];
    });
    this.errorsToCheck['no-inputs'] = !this.scenario?.enableProductSliders;
    this.validateTable();
  }

  /**
   * Performs validation checks on the percent change input.
   * @param input is the ChangePercent input object that is being validated.
   * @param emptyCheck is a boolean that determines if an empty input check should be performed.
   */
  checkPercentChangeInput(input: ChangePercent, emptyCheck = false) {
    this.setUnsavedChanges();
    input.errors = [];
    // check input validness
    if (emptyCheck && !Utils.isNumeric(input.value)) {
      input.errors.push('empty-input');
    }
    // update control
    const inputControl = this.sensForm?.controls[input.name];
    if (inputControl) {
      if (input.errors.length === 0) {
        inputControl.setErrors(null);
      } else {
        inputControl.setErrors({'incorrect': true});
      }
      inputControl.markAsTouched();
    }
    return input.errors.length === 0;
  }

  /**
   * Performs a percent change input check and if valid applies the percent change to all
   * applicable table items. Applies to selected table items unless there are no selected table items,
   * in which case all items will have the change applied.
   */
  applyPercentChange() {
    if (!this.checkPercentChangeInput(this.changePercent, true)) {
      return;
    }

    let revalidateTableData = false;

    for (const input of this.scenario.inputs) {
      if (this.selectedTableRows[input.uuid] || this.numOfSelectedRows() === 0) {
        if (this.changePercent?.value && input.baselineValue !== null) {
          const reverse = input.baselineValue >= 0 ? 1 : -1;
          input.scenarioValue = input.baselineValue * (100 + this.changePercent.value * reverse) * 0.01;
          revalidateTableData = true;
        }
      }
    }

    if (revalidateTableData) {
      this.validateTable();
    }
  }

  onEnableBiogasSliderChange(event: boolean) {
    this.setUnsavedChanges();
    this.errorsToCheck['no-inputs'] = !event;
    this.validateTable();
  }

  /**
   * Performs form validation and saves the current scenario.
   */
  onSaveClick() {
    this.errorsToCheck['empty-input'] = true;
    // the no inputs check isn't necessary if sliders are enabled since they will be added as inputs
    this.errorsToCheck['no-inputs'] = !this.scenario?.enableProductSliders;
    this.errorsToCheck['invalid-inputs'] = true;

    if (this.validateTable()) {
      // add biogas scenario inputs back to scenario inputs list
      if (this.scenario.enableProductSliders) {
        this.scenario.inputs.push(...this.biogasScenarioInputs);
      }
      if (this.scenarioComparisonService.saveScenario(this.scenario, this.editIndex)) {
        this.dialogRef.close();
      }
    }
  }

  /**
   * Closes the scenario modify popup.
   */
  onCancelClick() {
    this.dialogRef.close();
  }

  /**
   * Returns boolean for current state of form validity.
   */
  getFormValid(): boolean {
    return !!(this.formErrors.length === 0
      && this.scenario.name
      && (this.scenario.inputs?.length > 0
        || this.scenario.enableProductSliders && this.biogasSliderData?.length > 0)
    );
  }

  /**
   * Returns the appropriate test for the remove table inputs buttons based on if there are any
   * inputs selected.
   */
  getRemoveInputsBtnText() {
    if (this.numOfSelectedRows() > 0) {
      return 'SCENARIO_COMPARISON.SCENARIO_MODIFY.REMOVE_SELECTED_BTN';
    } else {
      return 'SCENARIO_COMPARISON.SCENARIO_MODIFY.REMOVE_ALL_BTN';
    }
  }

  /**
   * Creates a valid string that can be used for html input id properties.
   * @param name is the name of the input that will be used to create a unique id (as long as the
   * name is unique).
   * @param suffix will be used to create the id in conjunction with the name to help keep the id
   * unique.
   */
  getId(name: string, suffix: string) {
    if (!!name) {
      return name.replace(/ /g, '/__/') + suffix;
    }
  }

  /**
   * Extracts any biogas share scenario inputs from the scenario inputs list.
   * This should be performed before the scenario inputs are processed, the biogas
   * share inputs are extracted and used outside the main scenario input flow and are added
   * back before the save is performed. Biogas share inputs are modified using the biogas share
   * percentage slider component separate from the input selection and scenario input table.
   */
  extractBiogasShareInputs(): void {
    const biogasShareInputs: ScenarioInput[] = this.scenario.inputs.filter(
      (input) => input.path?.includes('biogasShareData')
    );
    for (const biogasShareInput of biogasShareInputs) {
      const index = this.scenario.inputs.indexOf(biogasShareInput);
      if (index > -1 && index < this.scenario.inputs.length) {
        this.scenario.inputs.splice(index, 1);
      }
    }
    this.biogasScenarioInputs = biogasShareInputs;
  }

  /**
   * Helper function to check if the biogasSliderData has changed from its
   * initial state when the dialog was first opened
   */
  private hasBiogasSliderDataChanged(sliderData: SliderData[]): boolean {
    if (sliderData.length !== this.initialBiogasSliderData.length) {
      return true;
    }

    for (let i = 0; i < sliderData.length; i++) {
      if (
        sliderData[i].name !== this.initialBiogasSliderData[i].name ||
        sliderData[i].type !== this.initialBiogasSliderData[i].type ||
        sliderData[i].value !== this.initialBiogasSliderData[i].value ||
        sliderData[i].uuid !== this.initialBiogasSliderData[i].uuid
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Updates the biogas scenario inputs when the biogas share slider distribution is changed.
   * This function ensure the biogas scenario inputs stay updated when changes are made.
   * @param sliderData is the array of slider data used to populate the scenario inputs.
   */
  biogasDistributionChanged(sliderData: SliderData[]): void {
    if (this.hasBiogasSliderDataChanged(sliderData)) {
      this.setUnsavedChanges();
    }

    this.biogasScenarioInputs = [];

    for (const percent of sliderData) {
      const inputs = new ScenarioInput();
      inputs.id = percent.type;
      inputs.nameElements = [percent.type];
      inputs.scenarioValue = percent.value;
      inputs.path = ['plantSpecifications', 'biogasShareData', percent.uuid ?? '', 'share', 'value'];
      this.biogasScenarioInputs.push(inputs);
    }
  }

  /**
   * Scenario modify component setup; If scenario service is ready, sets the current scenario
   * and begins initial input population, sorting, and table data population.
   * @private
   */
  private setup(): void {
    if (this.scenarioServiceReady) {
      // edit scenario; else adding new scenario
      if (this.dataIn.scenario) {
        this.scenario = this.dataIn.scenario;
        this.originalScenarioName = this.scenario.name;
        this.editIndex = this.dataIn.editIndex;
        this.invalidInputs = this.dataIn.invalidInputs;
        this.affectedInputs = this.dataIn.affectedInputs;
      } else {
        this.scenario = new Scenario();
      }

      // remove and set biogas share input before processing scenario inputs
      this.extractBiogasShareInputs();
      this.biogasShareData = this.scenarioComparisonService.getBiogasShareData();
      this.setBiogasSliders();

      // it's safe to process scenario inputs now
      this.getProjectInputs();
      this.sortAllInputs();
      this.validateTable();
    }
  }

  /**
   * Fetches updated project inputs and populates data objects as part of the initial
   * component setup.
   * @private
   */
  private getProjectInputs(): void {
    this.inputsDropdownData = this.scenarioComparisonService.getLatestResultStateScenarioInputs();
    this.populateInputTranslationsArray();
    this.populateProductPriceData();
    this.filteredInputsData = this.inputsDropdownData;
    this.updateExistingScenarioItems();
  }

  /**
   * Populates the input translations object with processed translations for each input
   * @private
   */
  private populateInputTranslationsArray(): void {
    for (const input of this.inputsDropdownData) {
      if (input.nameElements && input.nameElements.length > 0) {
        this.inputTranslations[input.id] = this.processInputName(input.nameElements);
      }
    }
  }

  /**
   * Processes the input name elements array provided and returns a translated string.
   * @param nameElements is an array of strings that will be processed and translated where possible.
   * @private
   */
  private processInputName(nameElements: string[]): string {
    let name = '';
    if (nameElements && nameElements.length > 0) {
      for (const element of nameElements) {
        let translatedElement = element;
        if (element.includes('.')) {
          translatedElement = this.translationService.get(element);
        }
        if (name === '') {
          name = translatedElement;
          continue;
        }
        name += ', ' + translatedElement;
      }
    }
    return name;
  }

  /**
   * Populates product price and lcfsrin data arrays. They are used for various parts of the
   * component to manipulate product input states in the input selection dropdown.
   * @private
   */
  private populateProductPriceData(): void {
    // find generated products price inputs
    this.productPriceData = this.inputsDropdownData.filter(input => this.isProductPrice(input.path));
    // find generated products lcfs/rin inputs
    this.productLcfsRinData = this.inputsDropdownData.filter(input => this.isLcfsRin(input.path));

  }

  /**
   * Returns boolean for if the provided path is a product price input.
   * @param path is an array of strings that is processed to determine if the path belongs to
   * a product price input.
   * @private
   */
  private isProductPrice(path?: string[]): boolean {
    if (!path) return false;
    return path[0] === 'plantSpecifications' && path[1] === 'generatedProducts' && path[3] === 'price';
  }

  /**
   * Returns boolean for if the provided path is a product lcfsrin input.
   * @param path is an array of strings that is processed to determine if the path belongs to
   * a product lcfsrin input.
   * @private
   */
  private isLcfsRin(path?: string[]): boolean {
    if (!path) return false;
    return path[0] === 'plantSpecifications' && path[1] === 'generatedProducts' && path[3] === 'lcfsRin';
  }

  /**
   * Checks the current scenarios scenario items against the updated list of inputs. If an input
   * match is found, the updated input is updated with the existing scenario items scenario value.
   * If no match is found the input is no longer valid.
   */
  private updateExistingScenarioItems() {
    // if no scenario items this can be skipped, there are no inputs to update
    if (!this.scenario?.inputs || this.scenario.inputs.length < 1) return;

    const tempItems: ScenarioInput[] = [];
    // for each input, check for updated input data values
    for (let i = 0; i < this.scenario.inputs.length; i++) {
      const scenarioInput = this.scenario.inputs[i];
      const updatedInputData = this.inputsDropdownData.find(i => i.id === scenarioInput.id);
      // if found an updated input
      if (updatedInputData) {
        // set new items scenario value to existing scenario value
        updatedInputData.scenarioValue = scenarioInput.scenarioValue;
        tempItems.push(updatedInputData);
      } else {
        // this input is invalid and doesn't exist anymore but the user will still see it
        // and be able to remove it themselves for transparency
         tempItems.push(scenarioInput);
      }
    }
    // set scenario items to updated inputs array;
    this.scenario.inputs = tempItems;
  }

  /**
   * Sorts all inputs in the top level input data object based on their
   * translated id's alphabetically.
   * @private
   */
  private sortAllInputs() {
    if (this.inputsDropdownData) {
      this.inputsDropdownData.sort((a: ScenarioInput, b: ScenarioInput) => {
        if (this.inputTranslations[a.id] < this.inputTranslations[b.id]) {
          return -1;
        }
        if (this.inputTranslations[a.id] > this.inputTranslations[b.id]) {
          return 1;
        }
        return 0;
      });
    }
  }

  /**
   * Validates each scenario item present in the table data. Once each input
   * is checked for validity summarise errors function is called to add them to the form.
   * @private
   */
  private validateTable() {
    let overallValid = true;
    let inputIsValid = true;
    // table inputs check
    for (const tableItem of this.scenario.inputs) {
      if (tableItem.id) {
        inputIsValid = this.checkInput(tableItem);
        overallValid = overallValid && inputIsValid;
      }
    }

    // this order matters, summariseErrors has to be called
    overallValid =  this.summariseErrors() && overallValid;
    return overallValid;
  }

  /**
   * Check the provided scenario item for multiple validation violations and return
   * boolean a based on if any errors are present at all, regardless of the current input.
   * @param input is a scenario item that is being checked for validity.
   * @private
   */
  private checkInput(input: ScenarioInput): boolean {
    this.checkNaNInput(input);
    if (!input.nameElements[input.nameElements.length - 1].includes('PROJECT.FINANCIAL_GEN_PROD.LCFS.PCI')) {
      this.checkNegativeInput(input);
    }
    this.checkPercentInput(input);
    if (this.errorsToCheck['empty-input']) {
      this.checkEmptyInput(input);
    }
    this.checkFinancialInput(input);
    this.updateInputsValidity(input);
    return this.getErrorHintsLength() === 0;
  }

  /**
   * Checks the current input for NaN validity. Existing errors are removed if the input is valid.
   * @param input is a scenario item that is being validated.
   * @private
   */
  private checkNaNInput(input: ScenarioInput) {
    if ((input.scenarioValue || input.scenarioValue === 0) && Number.isNaN(input.scenarioValue)) {
      this.scenarioValueErrorHints[input.uuid] = {[ScenarioModifyErrorHintIds.IS_NAN]: true};
    } else {
      if (this.scenarioValueErrorHints[input.uuid]) {
        delete this.scenarioValueErrorHints[input.uuid][ScenarioModifyErrorHintIds.IS_NAN];
      }
    }
  }

  /**
   * Checks the current input for negative validity. Existing errors are removed if the
   * input is valid.
   * @param input is a scenario items that is being validated.
   * @private
   */
  private checkNegativeInput(input: ScenarioInput) {
    if (input.scenarioValue && input.scenarioValue < 0) {
      this.scenarioValueErrorHints[input.uuid] = {[ScenarioModifyErrorHintIds.NEGATIVE_INPUT]: true};
    } else {
      if (this.scenarioValueErrorHints[input.uuid]) {
        delete this.scenarioValueErrorHints[input.uuid][ScenarioModifyErrorHintIds.NEGATIVE_INPUT];
      }
    }
  }

  /**
   * Checks the current input for percent validity. Existing errors are removed if the input
   * is valid. If the inputs unit is not either a % or the percentage translation
   * path it is ignored.
   * @param input is a scenario item that is being validated.
   * @private
   */
  private checkPercentInput(input: ScenarioInput) {
    if (input.unit !== '%' && input.unit !== 'DATABASE.UNITS.PERCENT.PERCENT') {
      return;
    }

    if (input.scenarioValue && input.scenarioValue > 100) {
      this.scenarioValueErrorHints[input.uuid] = {[ScenarioModifyErrorHintIds.PERCENT_OVER_100]: true};
    } else {
      if (this.scenarioValueErrorHints[input.uuid]) {
        delete this.scenarioValueErrorHints[input.uuid][ScenarioModifyErrorHintIds.PERCENT_OVER_100];
      }
    }
  }

  /**
   * Checks the current input for emptiness validity. Existing errors are removed if the input
   * is valid.
   * @param input is a scenario item that is being validated.
   * @private
   */
  private checkEmptyInput(input: ScenarioInput) {
    if (!(input.scenarioValue || input.scenarioValue === 0)) {
      this.scenarioValueErrorHints[input.uuid] = {[ScenarioModifyErrorHintIds.EMPTY_INPUT]: true};
    } else {
      if (this.scenarioValueErrorHints[input.uuid]) {
        delete this.scenarioValueErrorHints[input.uuid][ScenarioModifyErrorHintIds.EMPTY_INPUT];
      }
    }
  }

  /**
   * Checks the current input for financial validity. Existing errors are removed if the
   * input is valid. If the input doesn't contain a financial keyword in its id the input
   * is ignored.
   * @param input is a scenario item that is being validated.
   * @private
   */
  private checkFinancialInput(input: ScenarioInput) {
    if (input.id?.includes('amortization')) {
      const lifetime = this.projectFinService.getLongestCapexLifetime();

      if (!lifetime || !lifetime?.value) {
        return;
      }

      if (input.scenarioValue) {
        if (input.scenarioValue > lifetime.value) {
          this.scenarioValueErrorHints[input.uuid][ScenarioModifyErrorHintIds.EXCEEDS_PROJECT_LIFETIME] = true;
        } else {
          if (this.scenarioValueErrorHints[input.uuid]) {
            delete this.scenarioValueErrorHints[input.uuid][ScenarioModifyErrorHintIds.EXCEEDS_PROJECT_LIFETIME];
          }
        }
        if (input.scenarioValue < 1) {
          this.scenarioValueErrorHints[input.uuid][ScenarioModifyErrorHintIds.SMALL_LOAN_DURATION] = true;
        } else {
          if (this.scenarioValueErrorHints[input.uuid]) {
            delete this.scenarioValueErrorHints[input.uuid][ScenarioModifyErrorHintIds.SMALL_LOAN_DURATION];
          }
        }
      }
    }
  }

  /**
   * Updates an inputs for validity state based on errors being present the error hints object.
   * @param input is a scenario item that is used to set form control validity.
   * @private
   */
  private updateInputsValidity(input: ScenarioInput) {
    if (this.sensForm) {
      const valueInput = this.sensForm.controls[input.id + '-scenario-value'];
      if (valueInput) {
        if (this.getErrorHintsLength() === 0) {
          valueInput.setErrors(null);
        } else {
          valueInput.setErrors({'incorrect-value': true});
        }
        valueInput.markAsTouched();
      }
      setTimeout(() => { // setTimeout is needed here because angular change detection
        this.sensForm?.form.markAllAsTouched();
      });
    }
  }

  /**
   * Returns the length of the error hints object.
   * @private
   */
  private getErrorHintsLength() {
    let counter = 0;
    for (const inputKey of Object.keys(this.scenarioValueErrorHints)) {
      if (Object.keys(this.scenarioValueErrorHints[inputKey])?.length > 0) {
        counter++;
      }
    }
    return counter;
  }

  /**
   * Summarizes present errors and adds them to the form.
   * @private
   */
  private summariseErrors() {
    this.formErrors = [];
    let isValid = true;
    // no inputs check
    if (this.errorsToCheck['no-inputs'] && this.scenario.inputs.length === 0) {
      this.addFormError('no-inputs');
      isValid = false;
    } else {
      this.removeFormError('no-inputs');
    }
    isValid = isValid && this.checkInvalidInputStatus();
    // table inputs check
    for (const input of this.scenario.inputs) {
      if (input.id) {
        const tableItemErrorHints: Record<string, boolean> = this.scenarioValueErrorHints[input.uuid];
        if (tableItemErrorHints) {
          for (const error of Object.keys(tableItemErrorHints)) {
            if (tableItemErrorHints[error]) {
              this.addFormError(error);
              isValid = false;
            }
          }
        }
      }
    }
    return isValid;
  }

  /**
   * Adds the provided error name to the form errors array if it doesn't exist already.
   * @param errName is the name of the error being added to the list of errors.
   * @private
   */
  private addFormError(errName: string) {
    if (!this.formErrors.includes(errName)) {
      this.formErrors.push(errName);
    }
  }

  /**
   * Removes the provided error name from the form errors array if it exists.
   * @param errName is the name of the error being removed from the list of errors.
   * @private
   */
  private removeFormError(errName: string) {
    let index = this.formErrors.indexOf(errName);
    while (index > -1) {
      this.formErrors.splice(index, 1);
      index = this.formErrors.indexOf(errName);
    }
  }

  /**
   * Check if there are any invalid inputs present in the current list of scenario inputs.
   */
  checkInvalidInputStatus(): boolean {
    if (!this.invalidInputs) return true;
    for (const input of this.scenario.inputs) {
      if (this.invalidInputs.includes(input.id)) {
        this.addFormError('invalid-inputs');
        return false;
      }
    }
    this.removeFormError('invalid-inputs');
    return true;
  }

  /**
   * Sorts all selected inputs based on processed translations alphabetically.
   * @private
   */
  private sortSelectedInputs() {
    this.scenario.inputs.sort((a: ScenarioInput, b: ScenarioInput) => {
      if (this.inputTranslations[a.id] < this.inputTranslations[b.id]) {
        return -1;
      }
      if (this.inputTranslations[a.id] > this.inputTranslations[b.id]) {
        return 1;
      }
      return 0;
    });
  }

  /**
   * Selects all viable options in the list of filtered inputs. In the case of product
   * prices vs lcfsrin inputs, a product price is selected unless there is an already selected
   * lcfsrin input for a single product uuid found with an input's path. Product price will not
   * be selected if an lcfsrin input is selected. The disabled state of product prices and lcfsrin
   * inputs will also be updated accordingly.
   * @private
   */
  private selectAllItems() {
    if (this.filteredInputsData && this.inputsDropdownData) {

      // create a list of already selected lcfsrin inputs
      // using the product uuid part of the path
      const preActiveLcfsrinProducts: string[] = [];
      for (const selectedInput of this.scenario.inputs) {
        if (this.isLcfsRin(selectedInput.path)) {
          if (!preActiveLcfsrinProducts.includes(selectedInput.path[2])) {
            preActiveLcfsrinProducts.push(selectedInput.path[2]);
          }
        }
      }

      // for each filtered input
      for (const item of this.filteredInputsData) {
        // if lcfsrin product, disable that products price input
        if (this.isLcfsRin(item.path)) {
          // if this input is of the already selected lcfsrin product
          // then select it as well and disable the product price
          if (!preActiveLcfsrinProducts.includes(item.path[2])) continue;
          this.updateProductPriceState(true, item.path);
          if (!this.scenario.inputs.includes(item)) {
            this.scenario.inputs.push(item);
          }
          // if input is product price, and we already know the lcfsrin's active
          // check if the product is the same product uuid
          // if so continue, else add the product price and disable the products lcfsrin inputs
        } else if (this.isProductPrice(item.path)) {
          if (preActiveLcfsrinProducts.includes(item.path[2])) continue;
          this.updateLcfsrinState(true, item.path);
          if (!this.scenario.inputs.includes(item)) {
            this.scenario.inputs.push(item);
          }
        } else {
          // else add every input
          if (!this.scenario.inputs.includes(item)) {
            this.scenario.inputs.push(item);
          }
        }
      }
    }
  }

  /**
   * Deselect all items currently displayed in the input dropdown.
   * @private
   */
  private deselectAllItems() {
    if (this.filteredInputsData && this.inputsDropdownData
      && this.filteredInputsData.length === this.inputsDropdownData.length
    ) {
      this.scenario.inputs = [];
    } else {
      for (const item of this.filteredInputsData) {
        const index = this.scenario.inputs.indexOf(item);
        if (index > -1) {
          this.scenario.inputs.splice(index, 1);
        }
      }
    }
  }

  /**
   * Updates the disabled state of product price inputs based on the provided disabled state and
   * input path.
   * @param disabled is a boolean that determines if the product price is being disabled.
   * @param path is the string array representation of the current input's path. This is used to find
   * matching product price inputs by checking the path location of the generated product uuid.
   * @private
   */
  private updateProductPriceState(disabled: boolean, path: string[]) {
    for (const aInput of this.productPriceData) {
      // match the product uuid in the path to disable the price for the current lcfsrin input
      if (aInput.path[2] === path[2]) {
        this.disabled[aInput.uuid] = disabled;

        if (disabled) {
          this.tooltips[aInput.uuid] = 'SCENARIO_COMPARISON.SCENARIO_MODIFY.TOOLTIPS.LCFSRIN_VS_PRODUCT_PRICE';
        } else {
          delete this.tooltips[aInput.uuid];
        }
      }
    }
  }

  /**
   * Updates the disabled state of product lcfsrin inputs based on the provided disabled state and
   * input path.
   * @param disabled is a boolean that determines if the product price is being disabled.
   * @param path is the string array representation of the current input's path. This is used to find
   * matching product lcfsrin inputs by checking the path location of the generated product uuid.
   * @private
   */
  private updateLcfsrinState(disabled: boolean, path: string[]) {
    for (const aInput of this.productLcfsRinData) {
      // match the products uuid in the path
      if (aInput.path[2] === path[2]) {
        this.disabled[aInput.uuid] = disabled;

        if (disabled) {
          this.tooltips[aInput.uuid] = 'SCENARIO_COMPARISON.SCENARIO_MODIFY.TOOLTIPS.LCFSRIN_VS_PRODUCT_PRICE';
        } else {
          delete this.tooltips[aInput.uuid];
        }
      }
    }
  }

  /**
   * Check the state of the product price given the provided input. If the input is a product
   * price input the lcfsrin disabled state is cleared for the matching generated product uuid
   * found in the input's path. Otherwise, the product price disabled state for the matching
   * generated product. This function should be used as a fallback with an input has already
   * been checked for lcfsrin and product price being enabled.
   * product uuid
   * @param input is a scenario item that will be checked for the matching generated
   * product uuid if found within it's path
   * @private
   */
  private checkProductPriceEnabledState(input: ScenarioInput) {
    if (this.isProductPrice(input.path)) {
      for (const aInput of this.productLcfsRinData) {
        if (aInput.path[2] === input.path[2]) {
          delete this.disabled[aInput.uuid];
          delete this.tooltips[aInput.uuid];
        }
      }
    } else {
      for (const aInput of this.productPriceData) {
        if (aInput.path[2] === input.path[2]) {
          delete this.disabled[aInput.uuid];
          delete this.tooltips[aInput.uuid];
        }
      }
    }
  }

  /**
   * Returns a boolean that determines if any lcfsrin input is currently active based on the
   * generated product uuid found with the provided input's path.
   * @param input is a scenario item that will be used to check for any lcfsrin inputs that are
   * enabled and associated with it's generated product uuid if applicable.
   * @param checked is the checked state of the provided input.
   * @private
   */
  private lcfsrinInputsEnabledForProduct(input: ScenarioInput, checked: boolean): boolean {
    // if current input is lcfsrin input and being checked
    // it's safe to assume the current product should have its price disabled
    if (this.isLcfsRin(input.path) && checked) return true;

    // check for already selected lcfsrin inputs for the current product
    for (const item of this.scenario.inputs) {
      if (this.isLcfsRin(item.path) && item.path[2] === input.path[2] && item.uuid !== input.uuid) {
        return true;
      }
    }
    return false;
  }

  /**
   * This function processes and set biogas share scenario inputs. The most recent biogas share
   * data is used to create new biogas share slider data, which values are populated using
   * any existing biogas scenario input values that are present in the scenario. This function
   * also ensures any old data that isn't present anymore is sanitized.
   * @private
   */
  private setBiogasSliders() {
    this.biogasSliderData = [];
    this.biogasSlidersAvailable = false;
    if (this.biogasShareData) {
      // create new slider items from current project slider data
      for (const biogasShare of this.biogasShareData) {
        const newSliderDateItem = new SliderData(biogasShare);
        this.biogasSliderData.push(newSliderDateItem);
      }

      // remove biogas scenario inputs that no longer exist
      this.purgeBiogasScenarioInputs();

      // search for matching scenario inputs and if any are found update the value
      // of the new slider data
      for (const item of this.biogasSliderData) {
        // search for matching biogas scenario input
        const biogasShareInput = this.biogasScenarioInputs.find(
          scenarioInput => scenarioInput.id === item.name
        );
        // if found set the value to previous scenario value
        if (biogasShareInput) {
          item.value = biogasShareInput.scenarioValue;
        }
      }
      // there needs to be 2 data options available here
      this.biogasSlidersAvailable = (this.biogasSliderData.length > 1);
    }
    this.biogasSliderData = [...this.biogasSliderData];
    this.initialBiogasSliderData = this.biogasSliderData.map(item => ({ ...item }));    // Deep copy of biogasSliderData
  }

  /**
   * This function removes any biogas share scenario inputs that are no longer
   * present in the biogas share data.
   * @private
   */
  private purgeBiogasScenarioInputs() {
    const validInputs: ScenarioInput[] = [];
    // check for previously selected scenario inputs that have been removed
    for (const input of this.biogasScenarioInputs) {
      if (this.biogasSliderData.find(sliderData => sliderData.name === input.id)) {
        validInputs.push(input);
      }
    }
    this.biogasScenarioInputs = validInputs;
  }

  /**
   * Clears the scenario value of any input in the filteredInputsData that isn't currently
   * selected. The filtered inputs data array is used to populate the selected scenario items
   * using two-way binding so the inputs are passed by reference. This function will clear any
   * scenario values found in inputs that are not part of the currently selected inputs.
   * @private
   */
  private clearUnselectedInputValues(): void {
    for (const filteredInput of this.filteredInputsData) {
      if (filteredInput.scenarioValue !== null
        && filteredInput.scenarioValue !== undefined
        && !this.scenario.inputs.includes(filteredInput)) {
        filteredInput.scenarioValue = null;
      }
    }
  }

  getScenarioHeader() {
    if (Utils.isNumeric(this.editIndex) && this.editIndex > -1) {
      return this.translationService.get(
        'SCENARIO_COMPARISON.SCENARIO_MODIFY.EDIT_SCENARIO_HEADER',
        {param: this.originalScenarioName});
    } else {
      return this.translationService.get('SCENARIO_COMPARISON.SCENARIO_MODIFY.ADD_NEW_SCENARIO_HEADER');
    }
  }
}
